import React from "react";
import {
  Box,
  FormControlLabel,
  Switch,
  RadioGroup,
  Radio,
  Select,
  MenuItem,
  FormControl,
  TextField,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import {
  BusinessTemplateType,
  CustomSettings,
  isInvoiceOrProformaTemplateType,
  isProformaInvoiceTemplateType,
} from "subModule/src/services/invoice/types";
import { useUserLanguages } from "../hooks/useUserLanguages";
import {
  THERMAL_INVOICE_DEFAULTS,
  THERMAL_MAX_WIDTH_MM,
  normalizeThermalSectionOrder,
} from "../thermal/thermalInvoiceUtils";

interface GeneralSettingsPanelProps {
  settings: CustomSettings;
  onChange: (settings: Partial<CustomSettings>) => void;
  templateType?: number; // INVOICE = 2, JOBCARD = 3, WORKORDER = 4
}

const GeneralSettingsPanel: React.FC<GeneralSettingsPanelProps> = ({
  settings,
  onChange,
  templateType = 2,
}) => {
  const { t } = useTranslation();
  const { primaryLanguageName, secondaryLanguageName } = useUserLanguages();

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      {/* Language Display - Radio Buttons */}
      <Box>
        <label
          className="font-Saira font-[600] text-[13px] mb-2 block"
          style={{ color: "#333" }}
        >
          {t("languageDisplay")}
        </label>
        <RadioGroup
          row
          value={settings.languageDisplay || "both"}
          onChange={(e) => onChange({ languageDisplay: e.target.value as any })}
          sx={{ mt: 1 }}
        >
          <FormControlLabel
            value="both"
            control={
              <Radio
                size="small"
                sx={{ "&.Mui-checked": { color: "#4c0055" } }}
              />
            }
            label={t("both")}
          />
          <FormControlLabel
            value="primary"
            control={
              <Radio
                size="small"
                sx={{ "&.Mui-checked": { color: "#4c0055" } }}
              />
            }
            label={primaryLanguageName}
          />
          <FormControlLabel
            value="secondary"
            control={
              <Radio
                size="small"
                sx={{ "&.Mui-checked": { color: "#4c0055" } }}
              />
            }
            label={secondaryLanguageName}
          />
        </RadioGroup>
      </Box>

      {/* Date Format - Dropdown */}
      <Box>
        <label
          className="font-Saira font-[600] text-[13px] mb-2 block"
          style={{ color: "#333" }}
        >
          {t("dateFormat")}
        </label>
        <FormControl fullWidth size="small" sx={{ mt: 1 }}>
          <Select
            value={settings.dateFormat || "DD-MM-YYYY"}
            onChange={(e) => onChange({ dateFormat: e.target.value })}
          >
            <MenuItem value="DD-MM-YYYY">DD-MM-YYYY</MenuItem>
            <MenuItem value="MM-DD-YYYY">MM-DD-YYYY</MenuItem>
            <MenuItem value="YYYY-MM-DD">YYYY-MM-DD</MenuItem>
            <MenuItem value="DD/MM/YYYY">DD/MM/YYYY</MenuItem>
            <MenuItem value="MM/DD/YYYY">MM/DD/YYYY</MenuItem>
            <MenuItem value="YYYY/MM/DD">YYYY/MM/DD</MenuItem>
            <MenuItem value="DD MMM YYYY">DD MMM YYYY</MenuItem>
            <MenuItem value="MMM DD, YYYY">MMM DD, YYYY</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {/* Invoice print format & thermal options - INVOICE and PROFORMA */}
      {isInvoiceOrProformaTemplateType(templateType) && (
        <>
          <Box>
            <label
              className="font-Saira font-[600] text-[13px] mb-2 block"
              style={{ color: "#333" }}
            >
              {t("invoicePrintFormat")}
            </label>
            <FormControl fullWidth size="small" sx={{ mt: 1 }}>
              <Select
                value={settings.printFormat || "normal"}
                onChange={(e) => {
                  const value = e.target.value as "normal" | "thermal";
                  if (value === "thermal") {
                    onChange({
                      ...THERMAL_INVOICE_DEFAULTS,
                      printFormat: "thermal",
                      invoiceType: "normal",
                      sectionOrder: normalizeThermalSectionOrder(
                        settings.sectionOrder,
                      ),
                    });
                  } else {
                    onChange({ printFormat: "normal" });
                  }
                }}
              >
                <MenuItem value="normal">{t("normalPrint")}</MenuItem>
                <MenuItem value="thermal">{t("thermalPrint")}</MenuItem>
              </Select>
            </FormControl>
          </Box>

          {settings.printFormat === "thermal" && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <Box>
                <label
                  className="font-Saira font-[600] text-[13px] mb-2 block"
                  style={{ color: "#333" }}
                >
                  {t("thermalPaperWidth")}
                </label>
                <FormControl fullWidth size="small" sx={{ mt: 1 }}>
                  <Select
                    value={settings.thermalPaperWidthPreset || "80"}
                    onChange={(e) =>
                      onChange({
                        thermalPaperWidthPreset: e.target
                          .value as CustomSettings["thermalPaperWidthPreset"],
                      })
                    }
                  >
                    <MenuItem value="112">112 mm</MenuItem>
                    <MenuItem value="80">80 mm</MenuItem>
                    <MenuItem value="58">58 mm</MenuItem>
                    <MenuItem value="other">{t("other")}</MenuItem>
                  </Select>
                </FormControl>
              </Box>
              {settings.thermalPaperWidthPreset === "other" && (
                <TextField
                  size="small"
                  type="number"
                  label={t("customWidthMm")}
                  InputLabelProps={{ shrink: true }}
                  placeholder={t("customWidthMm")}
                  title={t("customWidthMm")}
                  value={settings.thermalCustomWidthMm ?? 80}
                  onChange={(e) => {
                    const raw = Number(e.target.value);
                    const clamped = Math.min(
                      Math.max(raw || 80, 40),
                      THERMAL_MAX_WIDTH_MM,
                    );
                    onChange({ thermalCustomWidthMm: clamped });
                  }}
                  inputProps={{ min: 40, max: THERMAL_MAX_WIDTH_MM, step: 1 }}
                  helperText={t("thermalWidthMaxHint", {
                    max: THERMAL_MAX_WIDTH_MM,
                  })}
                  fullWidth
                />
              )}
            </Box>
          )}

          {templateType === BusinessTemplateType.INVOICE &&
            settings.printFormat !== "thermal" && (
            <Box
              style={{
                fontWeight: "bold",
                fontSize: '17px',
                border: "2px dashed black",
                padding: "5px",
              }}
            >
              <FormControlLabel
                control={
                  <Switch
                    size="small"
                    checked={settings.invoiceType === "consolidated"}
                    onChange={(e) =>
                      onChange({
                        invoiceType: e.target.checked
                          ? "consolidated"
                          : "normal",
                      })
                    }
                    sx={{
                      "& .MuiSwitch-switchBase.Mui-checked": {
                        color: "#4c0055",
                      },
                    }}
                  />
                }
                label={
                  <span style={{ fontWeight: "bold", fontSize: '17px' }}>
                    {t("consolidatedInvoice") || "Consolidated Invoice"}
                  </span>
                }
                sx={{ m: 0, fontWeight: 600, color: "#333" }}
              />
            </Box>
          )}
        </>
      )}

      {/* Show Sections */}
      <Box>
        <label
          className="font-Saira font-[600] text-[13px] mb-2 block"
          style={{ color: "#333" }}
        >
          {t("showSections")}
        </label>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1, mt: 1 }}>
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={settings.showLogo ?? true}
                onChange={(e) => onChange({ showLogo: e.target.checked })}
                sx={{
                  "& .MuiSwitch-switchBase.Mui-checked": { color: "#4c0055" },
                }}
              />
            }
            label={t("logoSection")}
            sx={{ m: 0 }}
          />
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={settings.showHeader ?? true}
                onChange={(e) => onChange({ showHeader: e.target.checked })}
                sx={{
                  "& .MuiSwitch-switchBase.Mui-checked": { color: "#4c0055" },
                }}
              />
            }
            label={
              isProformaInvoiceTemplateType(templateType) &&
              settings.showCompanySection === false
                ? t("customerDetails")
                : t("companyCustomerDetails")
            }
            sx={{ m: 0 }}
          />
          {!isProformaInvoiceTemplateType(templateType) && (
            <FormControlLabel
              control={
                <Switch
                  size="small"
                  checked={settings.showVehicleInfo ?? true}
                  onChange={(e) =>
                    onChange({ showVehicleInfo: e.target.checked })
                  }
                  sx={{
                    "& .MuiSwitch-switchBase.Mui-checked": { color: "#4c0055" },
                  }}
                />
              }
              label={t("vehicleInfo")}
              sx={{ m: 0 }}
            />
          )}
          {/* Hide table section for inspection type */}
          {templateType !== 5 && (
            <FormControlLabel
              control={
                <Switch
                  size="small"
                  checked={settings.showTable ?? true}
                  onChange={(e) => onChange({ showTable: e.target.checked })}
                  sx={{
                    "& .MuiSwitch-switchBase.Mui-checked": { color: "#4c0055" },
                  }}
                />
              }
              label={t("tableSection")}
              sx={{ m: 0 }}
            />
          )}
          {/* Inspection-specific sections */}
          {templateType === 5 && (
            <>
              <FormControlLabel
                control={
                  <Switch
                    size="small"
                    checked={settings.showInspectionItems ?? true}
                    onChange={(e) => onChange({ showInspectionItems: e.target.checked })}
                    sx={{
                      "& .MuiSwitch-switchBase.Mui-checked": { color: "#4c0055" },
                    }}
                  />
                }
                label={t("inspectionItems")}
                sx={{ m: 0 }}
              />
              <FormControlLabel
                control={
                  <Switch
                    size="small"
                    checked={settings.showVehicleImage ?? true}
                    onChange={(e) => onChange({ showVehicleImage: e.target.checked })}
                    sx={{
                      "& .MuiSwitch-switchBase.Mui-checked": { color: "#4c0055" },
                    }}
                  />
                }
                label={t("vehicleImage")}
                sx={{ m: 0 }}
              />
              <FormControlLabel
                control={
                  <Switch
                    size="small"
                    checked={settings.showInspectionRemarks ?? true}
                    onChange={(e) =>
                      onChange({ showInspectionRemarks: e.target.checked })
                    }
                    sx={{
                      "& .MuiSwitch-switchBase.Mui-checked": { color: "#4c0055" },
                    }}
                  />
                }
                label={t("showInspectionRemarks")}
                sx={{ m: 0 }}
              />
              <FormControlLabel
                control={
                  <Switch
                    size="small"
                    checked={settings.showTechnicians ?? true}
                    onChange={(e) =>
                      onChange({ showTechnicians: e.target.checked })
                    }
                    sx={{
                      "& .MuiSwitch-switchBase.Mui-checked": { color: "#4c0055" },
                    }}
                  />
                }
                label={t("showTechnicians")}
                sx={{ m: 0 }}
              />
              <FormControlLabel
                control={
                  <Switch
                    size="small"
                    checked={settings.showCustomerSignature ?? true}
                    onChange={(e) => onChange({ showCustomerSignature: e.target.checked })}
                    sx={{
                      "& .MuiSwitch-switchBase.Mui-checked": { color: "#4c0055" },
                    }}
                  />
                }
                label={t("customerSignature")}
                sx={{ m: 0 }}
              />
            </>
          )}
          {!(
            isInvoiceOrProformaTemplateType(templateType) &&
            settings.printFormat === "thermal"
          ) && (
            <FormControlLabel
              control={
                <Switch
                  size="small"
                  checked={settings.showTerms ?? true}
                  onChange={(e) => onChange({ showTerms: e.target.checked })}
                  sx={{
                    "& .MuiSwitch-switchBase.Mui-checked": { color: "#4c0055" },
                  }}
                />
              }
              label={t("termsAndConditions")}
              sx={{ m: 0 }}
            />
          )}
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={settings.showNotes ?? true}
                onChange={(e) => onChange({ showNotes: e.target.checked })}
                sx={{
                  "& .MuiSwitch-switchBase.Mui-checked": { color: "#4c0055" },
                }}
              />
            }
            label={t("notesSection")}
            sx={{ m: 0 }}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default GeneralSettingsPanel;
